/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

/* eslint-disable max-len */

'use strict';

// MODULES //

import { isPrimitive as isNonNegativeInteger } from '@stdlib/assert-is-nonnegative-integer';
import isIterableLike from '@stdlib/assert-is-iterable-like';
import isCollection from '@stdlib/assert-is-collection';
import isArrayBuffer from '@stdlib/assert-is-arraybuffer';
import isPlainObject from '@stdlib/assert-is-plain-object';
import isBuffer from '@stdlib/assert-is-buffer';
import isGenericDataType from '@stdlib/ndarray-base-assert-is-generic-data-type';
import isBinaryDataType from '@stdlib/ndarray-base-assert-is-binary-data-type';
import isDataType from '@stdlib/ndarray-base-assert-is-data-type';
import hasOwnProp from '@stdlib/assert-has-own-property';
import buffer from '@stdlib/ndarray-base-buffer';
import typedarray from '@stdlib/array-typed';
import copy from '@stdlib/array-base-copy';
import ndarray from '@stdlib/ndarray-ctor';
import defaults from '@stdlib/ndarray-defaults';
import arraybuffer2ndarray from '@stdlib/ndarray-from-arraybuffer';
import array2buffer from '@stdlib/buffer-from-array';
import copyBuffer from '@stdlib/buffer-from-buffer';
import iterator2array from '@stdlib/array-from-iterator';
import bytesPerElement from '@stdlib/ndarray-base-bytes-per-element';
import ITERATOR_SYMBOL from '@stdlib/symbol-iterator';
import format from '@stdlib/error-tools-fmtprodmsg';


// VARIABLES //

var DEFAULT_DTYPE = defaults.get( 'dtypes.default' );
var DEFAULT_ORDER = defaults.get( 'order' );


// FUNCTIONS //

/**
* Resolves the order of the output vector.
*
* @private
* @param {*} options - options argument
* @returns {string} order
*/
function resolveOrder( options ) {
	if ( hasOwnProp( options, 'order' ) ) {
		return options.order;
	}
	return DEFAULT_ORDER;
}

/**
* Returns a vector having a specified data type.
*
* @private
* @param {(NonNegativeInteger|Collection|ArrayBuffer|Buffer|Iterable|Options)} arg - length, typed array, array-like object, buffer, iterable, or options object
* @param {string} dtype - data type
* @param {(Options|null)} options - function options
* @param {boolean} [options.readonly=false] - boolean indicating whether to return a read-only ndarray
* @param {string} [options.mode='throw'] - specifies how to handle indices which exceed ndarray dimensions
* @returns {(ndarray|null)} one-dimensional ndarray
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
*
* var v = vectorWithDType( 10, 'float64', {} );
* // returns <ndarray>
*
* var len = numel( v );
* // returns 10
*
* var dt = getDType( v );
* // returns 'float64'
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
*
* var v = vectorWithDType( [ 1, 2, 3, 4 ], 'generic', {} );
* // returns <ndarray>
*
* var len = numel( v );
* // returns 4
*
* var dt = getDType( v );
* // returns 'generic'
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 32 );
* var v = vectorWithDType( buf, 'float64', {} );
* // returns <ndarray>
*
* var len = numel( v );
* // returns 4
*
* var dt = getDType( v );
* // returns 'float64'
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
*
* var v = vectorWithDType( {}, 'float64', null );
* // returns <ndarray>
*
* var len = numel( v );
* // returns 0
*
* var dt = getDType( v );
* // returns 'float64'
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
* import array2iterator from '@stdlib/array-to-iterator';
*
* var v = vectorWithDType( array2iterator( [ 1, 2, 3, 4 ] ), 'generic', {} );
* // returns <ndarray>
*
* var len = numel( v );
* // returns 4
*
* var dt = getDType( v );
* // returns 'generic'
*/
function vectorWithDType( arg, dtype, options ) {
	var opts;
	var buf;

	// Note: in all of the following, we delegate option validation to the ndarray constructor...
	if ( options === null ) {
		opts = {};
	} else {
		opts = options;
	}
	// Case: vector( length )
	if ( isNonNegativeInteger( arg ) ) {
		buf = buffer( dtype, arg );
		return new ndarray( dtype, buf, [ buf.length ], [ 1 ], 0, resolveOrder( opts ), opts );
	}
	// Case: vector( Buffer )
	if ( isBuffer( arg ) ) {
		if ( isGenericDataType( dtype ) ) {
			buf = copy( arg );
		} else if ( isBinaryDataType( dtype ) ) {
			buf = copyBuffer( arg );
		} else {
			buf = typedarray( arg, dtype );
		}
		return new ndarray( dtype, buf, [ buf.length ], [ 1 ], 0, resolveOrder( opts ), opts );
	}
	// Case: vector( collection )
	if ( isCollection( arg ) ) {
		if ( isGenericDataType( dtype ) ) {
			buf = copy( arg );
		} else if ( isBinaryDataType( dtype ) ) {
			buf = array2buffer( arg ); // note: we assume that `arg` is an array of octets
		} else {
			buf = typedarray( arg, dtype );
		}
		return new ndarray( dtype, buf, [ buf.length ], [ 1 ], 0, resolveOrder( opts ), opts );
	}
	// Case: vector( ArrayBuffer )
	if ( isArrayBuffer( arg ) ) {
		return arraybuffer2ndarray( dtype, arg, [ arg.byteLength/bytesPerElement( dtype ) ], [ 1 ], 0, resolveOrder( opts ), opts );
	}
	// Case: vector( Iterable )
	if ( isIterableLike( arg ) ) {
		buf = arg[ ITERATOR_SYMBOL ]();
		buf = iterator2array( buf );
		if ( isBinaryDataType( dtype ) ) {
			buf = array2buffer( buf );
		} else if ( !isGenericDataType( dtype ) ) {
			buf = typedarray( buf, dtype );
		}
		return new ndarray( dtype, buf, [ buf.length ], [ 1 ], 0, resolveOrder( opts ), opts );
	}
	// Case: vector( options )
	if ( options === null && isPlainObject( arg ) ) {
		buf = buffer( dtype, 0 );
		return new ndarray( dtype, buf, [ buf.length ], [ 1 ], 0, resolveOrder( arg ), arg );
	}
	return null;
}


// MAIN //

/**
* Creates a vector (i.e., a one-dimensional ndarray).
*
* ## Notes
*
* -   This API is intended to match the conventions of `@stdlib/array/typed`, which has a similar signature.
*
* @param {(NonNegativeInteger|Collection|ArrayBuffer|Iterable)} [arg] - length, typed array, array-like object, buffer, or iterable
* @param {NonNegativeInteger} [byteOffset=0] - byte offset
* @param {NonNegativeInteger} [length] - view length
* @param {string} [dtype='float64'] - data type
* @param {Options} [options] - function options
* @param {boolean} [options.readonly=false] - boolean indicating whether to return a read-only vector
* @param {string} [options.mode='throw'] - specifies how to handle indices which exceed vector dimensions
* @param {string} [options.order='row-major'] - memory layout (either row-major or column-major)
* @throws {TypeError} first argument must be either a length, typed array, array-like object, buffer, iterable, data type, or options object
* @throws {TypeError} must provide valid options
* @returns {ndarray} one-dimensional ndarray
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
*
* var arr = vector();
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 0
*
* var dt = getDType( arr );
* // returns 'float64'
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
*
* var arr = vector( 2 );
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 2
*
* var dt = getDType( arr );
* // returns 'float64'
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
*
* var arr = vector( [ 1.0, 2.0 ] );
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 2
*
* var dt = getDType( arr );
* // returns 'float64'
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
*
* var arr = vector( [ 1.0, 2.0 ], 'float32' );
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 2
*
* var dt = getDType( arr );
* // returns 'float32'
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 32 );
* var arr = vector( buf );
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 4
*
* var dt = getDType( arr );
* // returns 'float64'
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 32 );
* var arr = vector( buf, 16 );
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 2
*
* var dt = getDType( arr );
* // returns 'float64'
*
* @example
* import getDType from '@stdlib/ndarray-dtype';
* import numel from '@stdlib/ndarray-numel';
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 64 );
* var arr = vector( buf, 16, 2 );
* // returns <ndarray>
*
* var len = numel( arr );
* // returns 2
*
* var dt = getDType( arr );
* // returns 'float64'
*/
function vector() {
	var nargs;
	var arg0;
	var arg1;
	var arg2;
	var arg3;
	var arg4;
	var buf;
	var out;

	nargs = arguments.length;

	// Case: vector()
	if ( nargs === 0 ) {
		buf = buffer( DEFAULT_DTYPE, 0 );
		return new ndarray( DEFAULT_DTYPE, buf, [ buf.length ], [ 1 ], 0, DEFAULT_ORDER );
	}
	arg0 = arguments[ 0 ];

	// Case: vector( arg0 );
	if ( nargs === 1 ) {
		if ( isDataType( arg0 ) ) {
			return vectorWithDType( 0, arg0, null );
		}
		out = vectorWithDType( arg0, DEFAULT_DTYPE, null );
		if ( out === null ) {
			throw new TypeError( format( '2H9IN', arg0 ) );
		}
		return out;
	}
	arg1 = arguments[ 1 ];

	// Case: vector( arg0, arg1 )
	if ( nargs === 2 ) {
		// Case: vector( dtype, options )
		if ( isDataType( arg0 ) ) {
			if ( arg1 === null ) {
				throw new TypeError( format( '2H92V', arg1 ) );
			}
			return vectorWithDType( 0, arg0, arg1 );
		}
		// Case: vector( arg0, dtype )
		if ( isDataType( arg1 ) ) {
			out = vectorWithDType( arg0, arg1, null );
			if ( out === null ) {
				throw new TypeError( format( '2H9IO', arg0 ) );
			}
			return out;
		}
		// Case: vector( ArrayBuffer, byteOffset )
		if ( isNonNegativeInteger( arg1 ) ) {
			if ( !isArrayBuffer( arg0 ) ) {
				throw new TypeError( format( '2H92B', arg0 ) );
			}
			return arraybuffer2ndarray( DEFAULT_DTYPE, arg0, [ (arg0.byteLength-arg1)/bytesPerElement( DEFAULT_DTYPE ) ], [ 1 ], arg1, DEFAULT_ORDER );
		}
		// Case: vector( arg0, options )
		if ( arg1 === null ) {
			throw new TypeError( format( '2H92V', arg1 ) );
		}
		out = vectorWithDType( arg0, DEFAULT_DTYPE, arg1 );
		if ( out === null ) {
			throw new TypeError( format( '2H9IO', arg0 ) );
		}
		return out;
	}
	arg2 = arguments[ 2 ];

	// Case: vector( arg0, arg1, arg2 )
	if ( nargs === 3 ) {
		// Case: vector( ArrayBuffer, byteOffset, dtype )
		if ( isDataType( arg2 ) ) {
			if ( !isArrayBuffer( arg0 ) ) {
				throw new TypeError( format( '2H92B', arg0 ) );
			}
			if ( !isNonNegativeInteger( arg1 ) ) {
				throw new TypeError( format( '2H92C', arg1 ) );
			}
			return arraybuffer2ndarray( arg2, arg0, [ (arg0.byteLength-arg1)/bytesPerElement( arg2 ) ], [ 1 ], arg1, DEFAULT_ORDER );
		}
		// Case: vector( arg0, dtype, options )
		if ( isDataType( arg1 ) ) {
			if ( arg2 === null ) {
				throw new TypeError( format( '2H92V', arg2 ) );
			}
			out = vectorWithDType( arg0, arg1, arg2 );
			if ( out === null ) {
				throw new TypeError( format( '2H9IO', arg0 ) );
			}
			return out;
		}
		// Case: vector( ArrayBuffer, byteOffset, arg2 )
		if ( !isArrayBuffer( arg0 ) ) {
			throw new TypeError( format( '2H92B', arg0 ) );
		}
		if ( !isNonNegativeInteger( arg1 ) ) {
			throw new TypeError( format( '2H92C', arg1 ) );
		}
		// Case: vector( ArrayBuffer, byteOffset, length )
		if ( isNonNegativeInteger( arg2 ) ) {
			return arraybuffer2ndarray( DEFAULT_DTYPE, arg0, [ arg2 ], [ 1 ], arg1, DEFAULT_ORDER );
		}
		// Case: vector( ArrayBuffer, byteOffset, options )
		if ( arg2 === null ) {
			throw new TypeError( format( '2H92V', arg2 ) );
		}
		return arraybuffer2ndarray( DEFAULT_DTYPE, arg0, [ (arg0.byteLength-arg1)/bytesPerElement( DEFAULT_DTYPE ) ], [ 1 ], arg1, resolveOrder( arg2 ), arg2 );
	}
	arg3 = arguments[ 3 ];

	// Case: vector( ArrayBuffer, byteOffset, arg2, arg3 )
	if ( nargs === 4 ) {
		if ( !isArrayBuffer( arg0 ) ) {
			throw new TypeError( format( '2H92B', arg0 ) );
		}
		if ( !isNonNegativeInteger( arg1 ) ) {
			throw new TypeError( format( '2H92C', arg1 ) );
		}
		// Case: vector( ArrayBuffer, byteOffset, length, dtype )
		if ( isNonNegativeInteger( arg2 ) ) {
			if ( isDataType( arg3 ) ) {
				return arraybuffer2ndarray( arg3, arg0, [ arg2 ], [ 1 ], arg1, DEFAULT_ORDER );
			}
			// Case: vector( ArrayBuffer, byteOffset, length, options )
			if ( !isPlainObject( arg3 ) ) {
				throw new TypeError( format( '2H92V', arg3 ) );
			}
			return arraybuffer2ndarray( DEFAULT_DTYPE, arg0, [ arg2 ], [ 1 ], arg1, resolveOrder( arg3 ), arg3 );
		}
		// Case: vector( ArrayBuffer, byteOffset, dtype, options )
		if ( !isDataType( arg2 ) ) {
			throw new TypeError( format( '2H9IP', arg2 ) );
		}
		if ( !isPlainObject( arg3 ) ) {
			throw new TypeError( format( '2H92V', arg3 ) );
		}
		return arraybuffer2ndarray( arg2, arg0, [ (arg0.byteLength-arg1)/bytesPerElement( arg2 ) ], [ 1 ], arg1, resolveOrder( arg3 ), arg3 );
	}
	arg4 = arguments[ 4 ];

	// Case: vector( ArrayBuffer, byteOffset, length, dtype, options )
	if ( !isArrayBuffer( arg0 ) ) {
		throw new TypeError( format( '2H92B', arg0 ) );
	}
	if ( !isNonNegativeInteger( arg1 ) ) {
		throw new TypeError( format( '2H92C', arg1 ) );
	}
	if ( !isNonNegativeInteger( arg2 ) ) {
		throw new TypeError( format( '2H92F', arg2 ) );
	}
	if ( !isDataType( arg3 ) ) {
		throw new TypeError( format( '2H9IQ', arg3 ) );
	}
	if ( !isPlainObject( arg4 ) ) {
		throw new TypeError( format( '2H92V', arg4 ) );
	}
	return arraybuffer2ndarray( arg3, arg0, [ arg2 ], [ 1 ], arg1, resolveOrder( arg4 ), arg4 );
}


// EXPORTS //

export default vector;
